import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ApiService } from './api.service';

@Component({
  selector: 'app-admin-quotes-list',
  standalone: true,
  imports: [CommonModule, RouterLink, FormsModule],
  template: `
    <div class="min-h-full overflow-y-auto admin-premium-bg">
      <div class="max-w-[1700px] mx-auto p-5">
        <div class="flex items-center justify-between mb-5 shrink-0">
          <div>
            <h1 class="text-xl font-semibold text-slate-950 tracking-tight">Quotes</h1>
            <p class="text-slate-500 text-xs">Manage and track customer quotations.</p>
          </div>
          <div class="flex items-center gap-2">
            <a routerLink="/admin/dispatch/ready" class="px-3 py-2 text-white rounded-lg text-xs font-semibold shadow-sm transition-opacity hover:opacity-90 flex items-center gap-2" style="background:linear-gradient(135deg,#2B9AD2,#1A6FA6);">
              <i class="ti ti-plus text-[16px]"></i> Create from Job
            </a>
          </div>
        </div>

        <div class="mb-4">
          <div class="inline-flex max-w-full gap-1 bg-white border border-[#E7EEF5] rounded-xl p-1 overflow-x-auto shadow-sm">
          @for (tab of statusTabs; track tab.value) {
            <button type="button" (click)="setStatus(tab.value)"
              class="px-3 py-2 rounded-lg text-xs font-semibold transition-colors cursor-pointer flex items-center gap-2 whitespace-nowrap shrink-0"
              [class.bg-[#EAF5FA]]="statusFilter === tab.value"
              [class.text-[#1A6FA6]]="statusFilter === tab.value"
              [class.text-slate-500]="statusFilter !== tab.value">
              <i class="ti text-[15px]" [class]="tab.icon"></i>
              {{ tab.label }}
            </button>
          }
          </div>
        </div>

        <div class="bg-white rounded-xl shadow-sm border border-[#E7EEF5] overflow-hidden">
          <div class="px-4 py-3 flex items-center gap-2 flex-wrap border-b border-slate-100">
            <div class="relative flex-1 min-w-[220px] max-w-sm">
              <i class="ti ti-search absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[16px]"></i>
              <input type="text" placeholder="Search quotes"
                     [(ngModel)]="searchQuery" (input)="onSearch()"
                     class="w-full h-9 pl-9 pr-3 rounded-lg text-xs border border-slate-200 bg-slate-50 focus:bg-white focus:outline-none focus:border-[#1A6FA6] placeholder:text-slate-400">
            </div>
            @if (isLoading()) {
              <i class="ti ti-loader animate-spin text-[#1A6FA6] text-lg"></i>
            }
          </div>

          <div class="overflow-x-auto min-h-[300px]">
            <table class="w-full text-left text-sm">
              <thead>
                <tr class="bg-slate-50/80 border-b border-slate-100">
                  <th class="px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Customer</th>
                  <th class="px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Quote</th>
                  <th class="px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Address</th>
                  <th class="px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Date</th>
                  <th class="px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest text-right">Amount</th>
                  <th class="px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest text-center">Status</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-100">
                @for (quote of quotes(); track quote.id) {
                  <tr class="hover:bg-slate-50 transition-colors cursor-pointer" [routerLink]="['/admin/financials/quotes', quote.id]">
                    <td class="px-4 py-3">
                      <div class="flex items-center gap-3 min-w-0">
                        <div class="w-9 h-9 rounded-lg bg-[#EAF5FA] text-[#1A6FA6] border border-[#D5EBF6] flex items-center justify-center font-semibold text-sm shrink-0">
                          {{ quote.customer_name ? quote.customer_name.charAt(0) : 'Q' }}
                        </div>
                        <div class="min-w-0">
                          <p class="font-semibold text-slate-900 text-sm truncate">{{ quote.customer_name || quote.quote_no || ('#' + quote.id) }}</p>
                          <p class="text-[10px] text-slate-400 font-semibold uppercase tracking-widest">ID #{{quote.id}}</p>
                        </div>
                      </div>
                    </td>
                    <td class="px-4 py-3">
                      <span class="font-mono text-xs font-semibold px-2 py-1 rounded-md bg-[#EAF5FA] text-[#1A6FA6] border border-[#D5EBF6]">{{ quote.quote_no }}</span>
                    </td>
                    <td class="px-4 py-3 text-slate-500 text-xs max-w-xs">
                      @if (quote.job_address) {
                        <div class="flex items-center gap-1.5 min-w-0">
                          <i class="ti ti-map-pin text-[14px] text-slate-400 shrink-0"></i>
                          <span class="truncate">{{ quote.job_address }}</span>
                        </div>
                      }
                    </td>
                    <td class="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">{{ quote.created_at ? (quote.created_at | date:'dd MMM yyyy') : '-' }}</td>
                    <td class="px-4 py-3 text-right font-semibold text-slate-900 text-sm whitespace-nowrap">{{ quote.total | currency:'NZD':'symbol':'1.2-2' }}</td>
                    <td class="px-4 py-3">
                      <div class="flex justify-center">
                        <span class="px-2.5 py-1 rounded-md text-[10px] font-semibold uppercase border"
                              [ngClass]="statusBadgeClass(quote.status)">
                          {{ quote.status || '-' }}
                        </span>
                      </div>
                    </td>
                  </tr>
                }
                @if (!isLoading() && quotes().length === 0) {
                  <tr>
                    <td colspan="6" class="py-16 text-center text-slate-400">
                      <i class="ti ti-file-dollar text-4xl mb-3 opacity-20"></i>
                      <p class="font-semibold text-sm">No quotes found.</p>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>

          <div class="px-4 py-3 flex items-center justify-between border-t border-slate-100">
            <span class="text-xs text-slate-400">Showing {{ isLoading() ? '...' : quotes().length }} quotes</span>
          </div>
        </div>
      </div>
    </div>
  `
})
export class AdminQuotesListComponent implements OnInit {
  private api = inject(ApiService);

  quotes = signal<any[]>([]);
  isLoading = signal(true);
  searchQuery = '';
  searchTimer: ReturnType<typeof setTimeout> | null = null;
  statusFilter = 'Draft';
  statusTabs = [
    { label: 'Draft', value: 'Draft', icon: 'ti-pencil' },
    { label: 'Sent', value: 'Sent', icon: 'ti-send' },
    { label: 'Accepted', value: 'Accepted', icon: 'ti-circle-check' },
    { label: 'Declined', value: 'Declined', icon: 'ti-circle-x' }
  ];

  ngOnInit() {
    this.loadQuotes();
  }

  loadQuotes() {
    this.isLoading.set(true);
    const search = this.searchQuery.trim();
    this.api.getQuotes({ search, status: search ? '' : this.statusFilter }).subscribe({
      next: (res: any) => {
        this.quotes.set(res.data.quotes || []);
        this.isLoading.set(false);
      },
      error: () => this.isLoading.set(false)
    });
  }

  onSearch() {
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.loadQuotes(), 250);
  }

  setStatus(status: string) {
    this.statusFilter = status;
    this.loadQuotes();
  }

  statusBadgeClass(status: string): string {
    if (status === 'Accepted') return 'text-emerald-700 bg-emerald-50 border-emerald-100';
    if (status === 'Sent') return 'text-sky-700 bg-sky-50 border-sky-100';
    if (status === 'Declined') return 'text-rose-700 bg-rose-50 border-rose-100';
    if (status === 'Draft') return 'text-slate-600 bg-slate-50 border-slate-200';
    return 'text-slate-500 bg-slate-50 border-slate-200';
  }
}
